import { useState, useRef } from 'react';
import { Upload, Image as ImageIcon, Crop, GripVertical, Trash2, Video } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { ImageCropDialog } from '@/components/ImageCropDialog';
import { OptimizationChip } from '@/components/OptimizationBadge';
import { uploadService } from '@/services/uploadService';
import { updateItemPhotoCrop, updateItemPhotoEnhancement } from '@/services/photoCropService';
import { PhotoEnhancementControls } from '@/components/admin/PhotoEnhancement';
import { photoToShow, usePhotoEnhancement } from '@/hooks/usePhotoEnhancement';
import { useAdminAuth } from '@/stores/adminAuthStore';
import { toast } from 'sonner';

interface ImageUploadProps {
  images: UploadedImage[];
  onImagesChange: (
    images: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])
  ) => void;
  maxImages?: number;
  disabled?: boolean;
  itemId?: string;
  onCropStart?: () => void;
  onCropSuccess?: (index: number, status: string) => void;
}

export interface UploadedImage {
  id?: number;
  url: string;
  originalUrl?: string;
  signedId?: string;
  filename?: string;
  uploading?: boolean;
  progress?: number;
  attachmentId?: string;
  cropData?: { x: number; y: number; width: number; height: number };
  viewportPosition?: { x: number; y: number };
  optimizationStatus?: 'red' | 'yellow' | 'green' | 'error';
  mediaType?: 'image' | 'video';
  // Postedición automática (spec 011)
  processedUrl?: string | null;
  enhancedUrl?: string | null;
  enhancementEnabled?: boolean;
}

interface SortableImageProps {
  image: UploadedImage;
  index: number;
  disabled: boolean;
  isProcessingCrop: boolean;
  onCropClick: (index: number) => void;
  onRemove: (index: number) => void;
  itemId?: string;
  canEdit: boolean;
}

function SortableImage({
  image,
  index,
  disabled,
  isProcessingCrop,
  onCropClick,
  onRemove,
  itemId,
  canEdit,
}: SortableImageProps) {
  const enhancement = usePhotoEnhancement(image.enhancementEnabled, (enabled) =>
    updateItemPhotoEnhancement(itemId ?? '', image.attachmentId ?? '', enabled)
  );
  const showEnhancement = Boolean(image.enhancedUrl && itemId && image.attachmentId && image.mediaType !== 'video');
  const shownUrl = showEnhancement
    ? photoToShow(image, enhancement.enabled, enhancement.showOriginal)
    : image.url;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isDragging ? 'z-50 scale-105 shadow-2xl' : ''}`}
    >
      {/* Position indicator */}
      <div className="absolute bottom-2 left-2 z-10 bg-content-primary text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
        {index + 1}
      </div>

      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute bottom-2 right-2 z-10 cursor-grab active:cursor-grabbing p-1.5 bg-white/90 hover:bg-white rounded shadow-md transition-colors"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar para reordenar"
      >
        <GripVertical className="h-4 w-4 text-gray-700" />
      </button>

      <div className="rounded-lg border overflow-hidden bg-gray-100 flex items-center justify-center aspect-[9/16]">
        {image.mediaType === 'video' ? (
          <div className="w-full h-full relative">
            <video
              src={image.url}
              className="w-full h-full object-cover"
              muted
              playsInline
            />
            <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded shadow-lg flex items-center gap-1">
              <Video className="h-3 w-3" />
              VIDEO
            </div>
          </div>
        ) : image.url ? (
          <img
            src={shownUrl}
            alt={image.filename || `Imagen ${index + 1}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <ImageIcon className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>

      {image.uploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
          <div className="w-3/4 bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-brand-primary h-full transition-all duration-300"
              style={{ width: `${image.progress || 0}%` }}
            />
          </div>
          <p className="text-white text-sm mt-2">
            {Math.round(image.progress || 0)}%
          </p>
        </div>
      )}

      {!image.uploading && (
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {image.optimizationStatus && image.mediaType !== 'video' && (
            <OptimizationChip status={image.optimizationStatus} />
          )}

          <div className="flex gap-2">
            {image.id && itemId && image.mediaType !== 'video' && (
              <button
                type="button"
                onClick={() => onCropClick(index)}
                disabled={disabled || isProcessingCrop}
                className="bg-blue-600 text-white rounded-full p-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                title="Optimizar"
                aria-label="Optimizar"
              >
                <Crop className="h-4 w-4" />
              </button>
            )}

            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={disabled}
              className="bg-red-600 text-white rounded-full p-2 hover:bg-red-700 transition-colors"
              aria-label="Eliminar"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {image.filename && !image.uploading && (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 truncate opacity-0 group-hover:opacity-100 transition-opacity">
          {image.filename}
        </div>
      )}

      {showEnhancement && (
        <PhotoEnhancementControls
          enabled={enhancement.enabled}
          saving={enhancement.saving}
          canEdit={canEdit}
          onToggle={enhancement.toggle}
          onCompare={enhancement.setShowOriginal}
        />
      )}
    </div>
  );
}

export function ImageUpload({
  images,
  onImagesChange,
  maxImages = 5,
  disabled = false,
  itemId,
  onCropStart,
  onCropSuccess,
}: ImageUploadProps) {
  const { admin } = useAdminAuth();
  const canEdit = admin?.role === 'owner' || admin?.role === 'manager';
  const [uploading, setUploading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageIndex, setCropImageIndex] = useState<number | null>(null);
  const [isProcessingCrop, setIsProcessingCrop] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id as string);
      const newIndex = parseInt(over.id as string);

      onImagesChange((prevImages) => arrayMove(prevImages, oldIndex, newIndex));
    }
  };

  const handleCropClick = (index: number) => {
    setCropImageIndex(index);
    setCropDialogOpen(true);
  };

  const handleCropSave = async (
    cropData: { x: number; y: number; width: number; height: number },
    viewportPosition: { x: number; y: number }
  ) => {
    if (cropImageIndex === null || !itemId) return;

    const image = images[cropImageIndex];
    if (!image.id || !image.attachmentId) {
      toast.error('No se puede procesar el crop: información de imagen incompleta');
      return;
    }

    setIsProcessingCrop(true);
    onCropStart?.();

    try {
      const result = await updateItemPhotoCrop(itemId, image.attachmentId, cropData, viewportPosition);

      onImagesChange((prevImages) =>
        prevImages.map((img, idx) =>
          idx === cropImageIndex
            ? {
              ...img,
              cropData,
              viewportPosition,
              optimizationStatus: (result.optimization_status as 'red' | 'yellow' | 'green' | 'error') || 'yellow',
              // El recorte nuevo invalida la versión editada hasta que se vuelva a generar
              enhancedUrl: null,
            }
            : img
        )
      );

      setCropDialogOpen(false);
      setCropImageIndex(null);
      onCropSuccess?.(cropImageIndex, result.optimization_status);
      toast.success('Crop enviado para procesamiento');
    } catch (error) {
      console.error('Error al procesar crop:', error);
      toast.error('Error al procesar el crop de la imagen');
    } finally {
      setIsProcessingCrop(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) return;

    if (images.length + files.length > maxImages) {
      toast.error(`Máximo ${maxImages} imágenes/videos permitidos`);
      return;
    }

    const validFiles = files.filter((file) => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        toast.error(`${file.name} no es una imagen o video válido`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);

    for (const file of validFiles) {
      try {
        const isVideo = file.type.startsWith('video/');
        const preview = URL.createObjectURL(file);
        const placeholder: UploadedImage = {
          url: preview,
          filename: file.name,
          uploading: true,
          progress: 0,
          mediaType: isVideo ? 'video' : 'image',
        };

        onImagesChange((prevImages) => [...prevImages, placeholder]);

        const signedId = await uploadService.uploadFile(file, (progress) => {
          onImagesChange((prevImages) =>
            prevImages.map((img) =>
              img.url === preview ? { ...img, progress } : img
            )
          );
        });

        onImagesChange((prevImages) =>
          prevImages.map((img) =>
            img.url === preview
              ? {
                  url: preview,
                  signedId,
                  filename: file.name,
                  uploading: false,
                  mediaType: isVideo ? 'video' : 'image',
                }
              : img
          )
        );

        toast.success(`${file.name} subida correctamente`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Error al subir ${file.name}`);

        onImagesChange((prevImages) =>
          prevImages.filter((img) => img.filename !== file.name)
        );
      }
    }

    setUploading(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onImagesChange(newImages);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const canUploadMore = images.length < maxImages;

  return (
    <div className="space-y-4">
      {canUploadMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || uploading}
          />
          <Button
            type="button"
            variant="outline"
            onClick={triggerFileInput}
            disabled={disabled || uploading}
            className="w-full"
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? 'Subiendo...' : 'Seleccionar Imágenes/Videos'}
          </Button>
          <p className="text-sm text-gray-500 mt-1">
            {images.length} de {maxImages} archivos. Arrastra para reordenar.
          </p>
        </div>
      )}

      {images.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((_, index) => index.toString())}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <SortableImage
                  key={index}
                  image={image}
                  index={index}
                  disabled={disabled}
                  isProcessingCrop={isProcessingCrop}
                  onCropClick={handleCropClick}
                  onRemove={handleRemove}
                  itemId={itemId}
                  canEdit={canEdit}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {images.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            No hay imágenes. Haz clic en "Seleccionar Imágenes" para añadir.
          </p>
        </div>
      )}

      {cropImageIndex !== null && cropDialogOpen && (
        <ImageCropDialog
          open={cropDialogOpen}
          onOpenChange={setCropDialogOpen}
          imageUrl={images[cropImageIndex]?.originalUrl || images[cropImageIndex]?.url || ''}
          onSave={handleCropSave}
          isProcessing={isProcessingCrop}
          initialCrop={images[cropImageIndex]?.cropData || null}
          initialViewport={
            images[cropImageIndex]?.viewportPosition?.x !== undefined &&
              images[cropImageIndex]?.viewportPosition?.y !== undefined
              ? images[cropImageIndex].viewportPosition
              : null
          }
        />
      )}
    </div>
  );
}
